import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Radium from 'radium';
import { connect } from 'react-redux';
import map from 'lodash/map';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { debounce } from 'globals/utils';
import { Company, Session, SatisfactionForm } from 'models';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';

import Avatar from 'shared/components/Avatar';
import * as keyboardEventHelpers from 'globals/keyboardEvents';

import {
  getActiveSession,
  getCompany,
  makeGetSessionStateSatisfactionBySessionId,
  makeGetSatisfactionFormByResponseId,
} from '../../app/ChatApp/selectors';
import * as chatAppActions from '../../app/ChatApp/actions';

import styles from './styles';
import messageStyles from '../messages/ChatMessageForUser/styles';
import messages from './messages';
import * as constants from './constants';

class ChatConversationSatisfactionSurvey extends PureComponent {
  constructor(props) {
    super(props);

    this._questionsDOMNode = null;
    this.debouncedUpdateSessionState = debounce(this.updateSessionState, 1000);

    this.state = {
      answers: keyBy(get(props.sessionState, 'answers'), 'id'),
      rating: get(props.sessionState, 'rating') || null,
      editing: false,
    };
  }

  componentDidMount() {
    const { onScrollToBottom } = this.props;

    onScrollToBottom();
  }

  componentDidUpdate(prevProps, prevState) {
    const { onScrollToBottom, onScrollToDOMNode } = this.props;

    const satisfactionLoaded = !prevProps.form.get('id') && this.props.form.get('id');

    if (satisfactionLoaded) {
      onScrollToBottom();
    }

    const ratingPicked = this.state.rating !== null;
    const ratingAdded = prevState.rating === null && ratingPicked;

    if (ratingAdded) {
      onScrollToDOMNode(this._questionsDOMNode);
      return;
    }

    const editingStateChanged = prevState.editing !== this.state.editing;

    if (editingStateChanged) {
      onScrollToBottom();
    }

    const sessionStateLoaded = !prevProps.sessionState && this.props.sessionState;

    if (sessionStateLoaded) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        answers: keyBy(get(this.props.sessionState, 'answers'), 'id'),
        rating: get(this.props.sessionState, 'rating') || null,
      });
    }
  }

  handleSubmitKeyPress = e => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.handleSubmit(e);
    }
  };

  updateSessionState = (sessionId, data) => {
    const { updateState } = this.props;

    updateState(sessionId, data);
  };

  handleSubmit = e => {
    const { sessionId, responseId, submit, clearState } = this.props;

    e.preventDefault();

    this.setState({ editing: false });

    submit(sessionId, responseId, {
      rating: this.state.rating,
      answers: Object.values(this.state.answers),
      submittedAt: new Date().toISOString(),
    });

    clearState(sessionId);
  };

  handleEditKeyPress = e => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.handleEdit(e);
    }
  };

  handleEdit = e => {
    e.preventDefault();

    this.setState({
      rating: null,
      answers: {},
      editing: true,
    });
  };

  handleSubmitRatingKeyPress = (e, rating) => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.handleSubmitRating(rating);
    }
  };

  handleSubmitRating = rating => {
    const { sessionId, responseId, submit, submitRating, form } = this.props;

    if (isEmpty(form.get('questions'))) {
      this.setState({ editing: false });

      submit(sessionId, responseId, {
        rating,
        answers: [],
        submittedAt: new Date().toISOString(),
      });
    } else {
      submitRating(sessionId, responseId, {
        rating,
      });
    }

    this.debouncedUpdateSessionState(sessionId, {
      rating,
      answers: Object.values(this.state.answers),
    });
    this.setState({ rating });
  };

  handleUpdateAnswer = (questionId, e) => {
    const { sessionId } = this.props;

    const answers = Object.assign({}, this.state.answers, {
      [questionId]: {
        id: questionId,
        answer: e.target.value,
      },
    });

    this.setState({
      answers,
    });
    this.debouncedUpdateSessionState(sessionId, {
      rating: this.state.rating,
      answers: Object.values(answers),
    });
  };

  renderQuestion = (question, index) => {
    const { intl } = this.props;
    const value = get(this.state, ['answers', question.id, 'answer']) || '';

    return (
      <div style={styles.question} key={`question_${question.id}`}>
        <label style={styles.questionLabel} htmlFor={`question_${index}`}>
          {question.prompt}
        </label>
        <textarea
          style={styles.questionField}
          id={`question_${index}`}
          name={`question_${index}_text`}
          rows={5}
          value={get(this.state, ['answers', question.id, 'answer'])}
          onChange={e => this.handleUpdateAnswer(question.id, e)}
          maxLength={constants.MAX_ANSWER_LENGTH}
          aria-label={intl.formatMessage(messages.aria_label_text_area_further_details)}
        />
        <span style={styles.questionFieldHint} aria-live="polite">
          <FormattedMessage
            {...messages.max_length}
            values={{ count: value.length, max: constants.MAX_ANSWER_LENGTH }}
          />
        </span>
      </div>
    );
  };

  renderQuestions = () => {
    if (this.state.rating === null) return null;

    const { form, intl } = this.props;

    if (isEmpty(form) || isEmpty(form.get('questions'))) return null;

    return (
      <form
        style={styles.questions}
        onSubmit={this.handleSubmit}
        ref={dom => {
          this._questionsDOMNode = dom;
        }}
      >
        {map(form.get('questions'), this.renderQuestion)}
        <button
          tabIndex="0"
          onKeyPress={this.handleSubmitKeyPress}
          onClick={this.handleSubmit}
          aria-label={intl.formatMessage(messages.aria_label_submit)}
          style={styles.submitButton}
        >
          <FormattedMessage {...messages.submit} />
        </button>
      </form>
    );
  };

  renderRating = () => {
    const { form } = this.props;

    const scaleOptions = form.get('scaleOptions');

    return (
      <div style={styles.rating}>
        <h3 style={styles.ratingPrompt}>{form.get('ratingPrompt')}</h3>
        <div style={styles.ratingOptionsWrapper}>
          <div style={styles.ratingOptions}>
            {constants.SCALE_MAP[scaleOptions].map((option, optionIndex) => {
              const style = this.state.rating === option ? 'color' : 'grey';

              const imgUrl = `https://cdn.kustomerapp.com/satisfaction/${form.get('scaleType')}_${option}_${style}.svg`;

              return (
                <img
                  tabIndex="0"
                  key={option}
                  src={imgUrl}
                  style={styles.ratingOption}
                  alt={`${optionIndex} / ${form.get('scaleOptions')}`}
                  onKeyPress={e => this.handleSubmitRatingKeyPress(e, option)}
                  role="button"
                  onClick={() => this.handleSubmitRating(option)}
                />
              );
            })}
          </div>
          <div style={styles.ratingLabels}>
            <span style={styles[`ratingLabelLeft_${scaleOptions > 3 ? 'wide' : 'narrow'}`]}>
              {form.get('scaleLabelLow')}
            </span>
            <span style={styles[`ratingLabelRight_${scaleOptions > 3 ? 'wide' : 'narrow'}`]}>
              {form.get('scaleLabelHigh')}
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderIntroduction = () => {
    const { form } = this.props;
    return (
      <div style={styles.intro}>
        <h3 style={styles.introHeading}>{form.get('introduction')}</h3>
      </div>
    );
  };

  renderForm = () => {
    return (
      <div style={styles.satisfaction}>
        {this.renderIntroduction()}
        {this.renderRating()}
        {this.renderQuestions()}
      </div>
    );
  };

  renderCompleted = () => {
    const { session } = this.props;

    const satisfactionLockedAt = session.get('satisfactionLockedAt');

    const parsedSatisfactionLockedAt = satisfactionLockedAt && Date.parse(satisfactionLockedAt);

    const editable = Date.now() < parsedSatisfactionLockedAt;

    return (
      <div style={styles.completed}>
        <i style={styles.completedIcon} />
        <div style={styles.completedText}>
          <FormattedMessage {...messages.feedback_thanks} />
        </div>
        {editable && (
          <a
            tabIndex="0"
            style={styles.completedEditButton}
            onKeyPress={this.handleEditKeyPress}
            onClick={this.handleEdit}
          >
            <FormattedMessage {...messages.edit} />
          </a>
        )}
      </div>
    );
  };

  renderMessageColumn = () => {
    const { session } = this.props;
    const { editing } = this.state;

    const commented = session.get('satisfactionStatus') === 'commented';
    const submitted = session.get('satisfactionSubmittedAt');

    return (
      <div
        className="userMessage kustomer-app-thread-user-message"
        style={Object.assign({}, messageStyles.message, styles.message)}
      >
        {!submitted || ((commented || submitted) && editing) ? this.renderForm() : this.renderCompleted()}
      </div>
    );
  };

  renderAvatarColumn = () => {
    const { company } = this.props;

    if (!company) return null;

    return (
      <div style={messageStyles.userAvatar}>
        <Avatar avatarUrl={company.avatarUrl} defaultName={company.name} />
      </div>
    );
  };

  render() {
    const { sessionId, form } = this.props;

    if (!sessionId || !form.get('id')) return null;

    return (
      <div style={messageStyles.wrapper}>
        {this.renderAvatarColumn()}
        {this.renderMessageColumn()}
      </div>
    );
  }
}

ChatConversationSatisfactionSurvey.propTypes = {
  company: PropTypes.instanceOf(Company),
  session: PropTypes.instanceOf(Session),
  sessionId: PropTypes.string.isRequired,
  sessionState: PropTypes.object, // eslint-disable-line react/forbid-prop-types, import/prefer-default-export
  responseId: PropTypes.string.isRequired,
  form: PropTypes.instanceOf(SatisfactionForm),
  submit: PropTypes.func.isRequired,
  submitRating: PropTypes.func.isRequired,
  updateState: PropTypes.func.isRequired,
  clearState: PropTypes.func.isRequired,
  onScrollToBottom: PropTypes.func.isRequired,
  onScrollToDOMNode: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

ChatConversationSatisfactionSurvey.defaultProps = {
  form: {},
};

export const makeMapStateToProps = () => {
  const getSessionStateSatisfactionBySessionId = makeGetSessionStateSatisfactionBySessionId();
  const getSatisfactionFormByResponseId = makeGetSatisfactionFormByResponseId();

  return state => {
    const session = getActiveSession(state);
    const responseId = session.get('satisfactionId');

    return {
      company: getCompany(state),
      session,
      sessionId: session.get('id'),
      sessionState: getSessionStateSatisfactionBySessionId(state, session.id),
      responseId,
      form: getSatisfactionFormByResponseId(state, responseId),
    };
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    submit(sessionId, responseId, data) {
      dispatch(
        chatAppActions.chatModifySession({
          id: sessionId,
          satisfactionStatus: 'commented',
          satisfactionSubmittedAt: data.submittedAt,
        }),
      );
      chatAppActions.submitSatisfactionResponse(responseId, data);
    },
    submitRating(sessionId, responseId, data) {
      dispatch(
        chatAppActions.chatModifySession({
          id: sessionId,
          satisfactionStatus: 'rated',
        }),
      );
      chatAppActions.submitSatisfactionResponse(responseId, data);
    },
    updateState(sessionId, satisfaction) {
      dispatch(chatAppActions.updateSessionStateSatisfaction(sessionId, satisfaction));
    },
    clearState(sessionId) {
      dispatch(chatAppActions.removeSessionStateSatisfaction(sessionId));
    },
  };
};

export const radiumComponent = Radium(ChatConversationSatisfactionSurvey);
export const componentWithIntl = injectIntl(radiumComponent, { withRef: true });

export default connect(
  makeMapStateToProps,
  mapDispatchToProps,
)(componentWithIntl);
